import React, { useContext, createContext, useState, useCallback } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GuidedTooltip from "../components/GuidedTooltip";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Button } from "@shadcn-in-nx/ui/button";
import { CheckCircle } from "lucide-react";
import { useBluelagoon } from "@bluelagoon";
import { useAuth } from "../context/AuthContext";

const tutorialContext = createContext();

function useProvideTutorial() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [selectedAutomation, setSelectedAutomation] = useState(null);
    const [automationPlan, setAutomationPlan] = useState(null);
    const [isTutorialActive, setIsTutorialActive] = useState(false);
    const [tutorialStep, setTutorialStep] = useState(null);

    // Guided automation state
    const [isGuidedMode, setIsGuidedMode] = useState(false);
    const [currentGuidedStep, setCurrentGuidedStep] = useState(0);
    const [guidedSteps, setGuidedSteps] = useState([]);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const [waitingForAction, setWaitingForAction] = useState(null);
    const [hasInitializedGuided, setHasInitializedGuided] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);

    const { call: markTutorialComplete, isLoading: isMarkingComplete } =
        useBluelagoon({
            method: "markTutorialComplete",
        });

    // Add this function to wait for elements using polling
    const waitForElement = useCallback(
        (selector, callback, timeout = 10000) => {
            const startTime = Date.now();

            const checkElement = () => {
                const element = document.querySelector(selector);
                if (element) {
                    callback(element);
                    return;
                }

                if (Date.now() - startTime > timeout) {
                    return;
                }

                // Check again in 100ms
                setTimeout(checkElement, 100);
            };

            checkElement();
        },
        []
    );

    // Enhanced version using MutationObserver
    const waitForElementWithObserver = useCallback(
        (selector, callback, timeout = 180000) => {
            let timeoutId;
            let observer;

            const cleanup = () => {
                if (timeoutId) clearTimeout(timeoutId);
                if (observer) observer.disconnect();
            };

            // Check if element already exists
            const existingElement = document.querySelector(selector);
            if (existingElement) {
                callback(existingElement);
                return cleanup;
            }

            // Set up MutationObserver to watch for new elements
            observer = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    if (mutation.type === "childList") {
                        const element = document.querySelector(selector);
                        if (element) {
                            cleanup();
                            callback(element);
                            return;
                        }
                    }
                }
            });

            // Start observing
            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });

            // Set timeout
            timeoutId = setTimeout(() => {
                cleanup();
            }, timeout);

            return cleanup;
        },
        []
    );

    // Initialize from localStorage if available
    React.useEffect(() => {
        const savedPlan = localStorage.getItem("automationPlan");
        const savedAutomation = localStorage.getItem("selectedAutomation");
        const hasCompletedTutorial = localStorage.getItem(
            "hasCompletedTutorial"
        );

        if (savedPlan) {
            try {
                setAutomationPlan(JSON.parse(savedPlan));
            } catch (error) {
                console.error("Failed to parse saved automation plan:", error);
            }
        }

        if (savedAutomation) {
            try {
                setSelectedAutomation(JSON.parse(savedAutomation));
            } catch (error) {
                console.error("Failed to parse saved automation:", error);
            }
        }

        // Set tutorial as active if not completed
        setIsTutorialActive(!hasCompletedTutorial);
    }, []);

    const selectAutomation = useCallback((automation) => {
        setSelectedAutomation(automation);
        localStorage.setItem("selectedAutomation", JSON.stringify(automation));
    }, []);

    const setTutorialPlan = useCallback((plan) => {
        setAutomationPlan(plan);
        localStorage.setItem("automationPlan", JSON.stringify(plan));
    }, []);

    const completeTutorialWithPlan = useCallback((plan) => {
        setAutomationPlan(plan);
        setIsTutorialActive(false);
        localStorage.setItem("automationPlan", JSON.stringify(plan));
        localStorage.setItem("hasCompletedTutorial", "true");
        markTutorialComplete({
            tutorialKey: "welcome",
            completed: true,
        });
    }, []);

    const clearTutorialData = useCallback(() => {
        setSelectedAutomation(null);
        setAutomationPlan(null);
        setIsTutorialActive(false);
        setTutorialStep(null);
        localStorage.removeItem("automationPlan");
        localStorage.removeItem("selectedAutomation");
        localStorage.removeItem("hasCompletedTutorial");
    }, []);

    const updateTutorialStep = useCallback((step) => {
        setTutorialStep(step);
    }, []);

    // Complete guided mode
    const completeGuidedMode = useCallback(() => {
        setIsGuidedMode(false);
        setIsTooltipVisible(false);
        setCurrentGuidedStep(0);
        setWaitingForAction(null);
        setHasInitializedGuided(false);
        clearTutorialData();
        setShowCompletionModal(true);
        markTutorialComplete({
            tutorialKey: "welcome",
            completed: true,
        });
    }, [clearTutorialData]);

    // Handle completion modal actions
    const handleMeetAutomationGuide = useCallback(() => {
        setShowCompletionModal(false);
        markTutorialComplete({
            tutorialKey: "welcome",
            completed: true,
        });
        navigate("/automation-guide");
    }, [navigate]);

    const handleContinueToWebTour = useCallback(() => {
        setShowCompletionModal(false);
        // Add logic to continue to web tour
        console.log("Continue to Web Tour clicked");
        markTutorialComplete({
            tutorialKey: "welcome",
            completed: true,
        });
    }, []);

    const closeCompletionModal = useCallback(() => {
        setShowCompletionModal(false);
        markTutorialComplete({
            tutorialKey: "welcome",
            completed: true,
        });
    }, []);

    // Show the current guided step
    const showNextGuidedStep = useCallback(
        (stepIndex, stepsArray = guidedSteps) => {
            // If no steps array or empty, return early
            if (!stepsArray || stepsArray.length === 0) {
                return;
            }

            if (stepIndex >= stepsArray.length) {
                completeGuidedMode();
                return;
            }

            const step = stepsArray[stepIndex];
            if (!step) {
                return;
            }

            // Handle info modals - show in center of screen
            if (step.action === "info") {
                setTooltipPosition({
                    x: window.innerWidth / 2,
                    y: window.innerHeight / 2,
                });
                setIsTooltipVisible(true);
                setWaitingForAction(step.action);
                return;
            }

            let margin = 10;

            if (step.action === "enter-prompt") {
                margin = 250;
            }

            // Use the enhanced element waiter with MutationObserver for other steps
            waitForElementWithObserver(step.targetSelector, (targetElement) => {
                const rect = targetElement.getBoundingClientRect();
                setTooltipPosition({
                    x: rect.left + rect.width / 2,
                    y:
                        step.action === "enter-prompt"
                            ? rect.bottom + margin
                            : rect.top - margin,
                });
                setIsTooltipVisible(true);
                setWaitingForAction(step.action);
            });
        },
        [guidedSteps, completeGuidedMode, waitForElementWithObserver]
    );

    // Initialize guided mode with automation plan
    const initializeGuidedMode = useCallback(
        (plan) => {
            const steps = plan.steps || [];
            const guidedInstructions = [];

            // Get trigger names from translations
            const triggerNames = {
                APP: t("tutorialContext.triggerNames.APP"),
                SCHEDULE: t("tutorialContext.triggerNames.SCHEDULE"),
                WEBHOOK: t("tutorialContext.triggerNames.WEBHOOK"),
                EMAIL: t("tutorialContext.triggerNames.EMAIL"),
                WHATSAPP: t("tutorialContext.triggerNames.WHATSAPP"),
            };

            steps.forEach((step, index) => {
                if (step.prompt) {
                    // Step 1: Click "Add Step"
                    guidedInstructions.push({
                        stepNumber: guidedInstructions.length + 1,
                        totalSteps: steps.length * 3, // Each prompt step has 3 guided steps
                        instruction: t(
                            "tutorialContext.guidedInstructions.addStep",
                            {
                                stepNumber: index + 1,
                            }
                        ),
                        action: "add-step",
                        targetSelector: '[data-guided="add-step"]',
                        stepData: step,
                    });

                    // Step 2: Enter the prompt
                    guidedInstructions.push({
                        stepNumber: guidedInstructions.length + 1,
                        totalSteps: steps.length * 3,
                        instruction: t(
                            "tutorialContext.guidedInstructions.enterPrompt",
                            {
                                prompt: step.prompt,
                            }
                        ),
                        action: "enter-prompt",
                        targetSelector: '[data-guided="chat-input"]',
                        stepData: step,
                    });

                    // Step 3: Click the send button
                    guidedInstructions.push({
                        stepNumber: guidedInstructions.length + 1,
                        totalSteps: steps.length * 3,
                        instruction: t(
                            "tutorialContext.guidedInstructions.sendPrompt"
                        ),
                        action: "send-prompt",
                        targetSelector: '[data-guided="chat-send"]',
                        stepData: step,
                    });

                    guidedInstructions.push({
                        stepNumber: guidedInstructions.length + 1,
                        totalSteps: steps.length * 3,
                        instruction: t(
                            "tutorialContext.guidedInstructions.waitForCoding"
                        ),
                        action: "run-code",
                        targetSelector: '[data-guided="generating-code"]',
                        stepData: step,
                    });

                    if (step.outputType === "html") {
                        // Step 3: Wait for completion and preview
                        guidedInstructions.push({
                            stepNumber: guidedInstructions.length + 1,
                            totalSteps: steps.length * 3,
                            instruction: t(
                                "tutorialContext.guidedInstructions.previewStep"
                            ),
                            action: "preview-step",
                            targetSelector: '[data-guided="preview-tab"]',
                            stepData: step,
                        });
                    } else {
                        // Step 2: Wait for completion and preview
                        guidedInstructions.push({
                            stepNumber: guidedInstructions.length + 1,
                            totalSteps: steps.length * 3,
                            instruction: t(
                                "tutorialContext.guidedInstructions.runCode"
                            ),
                            action: "run-code",
                            targetSelector: '[data-guided="run-code"]',
                            stepData: step,
                        });
                    }
                } else if (step.trigger) {
                    // Add trigger step
                    guidedInstructions.push({
                        stepNumber: guidedInstructions.length + 1,
                        totalSteps: steps.length * 3,
                        instruction: t(
                            "tutorialContext.guidedInstructions.addTrigger",
                            {
                                triggerName: triggerNames[step.trigger],
                            }
                        ),
                        action: "add-trigger",
                        targetSelector: '[data-guided="add-trigger"]',
                        stepData: step,
                    });
                } else if (step.info) {
                    guidedInstructions.push({
                        stepNumber: guidedInstructions.length + 1,
                        totalSteps: steps.length * 3,
                        instruction: step.info,
                        action: "info",
                        targetSelector: `[data-guided="${step.elementId}"]`,
                        stepData: step,
                    });
                } else if (step.waitForDataRefresh) {
                    guidedInstructions.push({
                        stepNumber: guidedInstructions.length + 1,
                        totalSteps: steps.length * 3,
                        instruction: t(
                            "tutorialContext.guidedInstructions.waitForDataRefresh"
                        ),
                        action: "wait-for-data-refresh",
                        targetSelector: '[data-guided="data-refresh"]',
                        stepData: step,
                    });
                } else if (step.newApp) {
                    guidedInstructions.push({
                        stepNumber: guidedInstructions.length + 1,
                        totalSteps: steps.length * 3,
                        instruction: step.instruction,
                        action: "new-app",
                        modal: true,
                        stepData: step,
                    });
                }
            });

            setGuidedSteps(guidedInstructions);
            setIsGuidedMode(true);
            setCurrentGuidedStep(0);

            // Start the guided tour
            setTimeout(() => {
                showNextGuidedStep(0, guidedInstructions);
            }, 1000);
        },
        [showNextGuidedStep, t]
    );

    // Handle guided step actions
    const handleGuidedAction = useCallback(
        (action) => {
            if (!isGuidedMode || waitingForAction !== action) return;

            // Ensure we have guided steps
            if (!guidedSteps || guidedSteps.length === 0) {
                return;
            }

            const currentStep = guidedSteps[currentGuidedStep];
            if (!currentStep) {
                return;
            }

            // Hide tooltip temporarily
            setIsTooltipVisible(false);
            setWaitingForAction(null);

            switch (action) {
                case "add-step": {
                    // The add step button was clicked, now wait for prompt entry
                    // const addStepButton = document.querySelector(
                    //     '[data-guided="add-step"]'
                    // );
                    // if (addStepButton) {
                    //     addStepButton.click();
                    // }
                    setTimeout(() => {
                        setCurrentGuidedStep(currentGuidedStep + 1);
                        showNextGuidedStep(currentGuidedStep + 1);
                    }, 500);
                    break;
                }

                case "enter-prompt": {
                    // Auto-fill the prompt in chat input
                    const chatInput = document.querySelector(
                        '[data-guided="chat-input"]'
                    );
                    if (chatInput && currentStep.stepData.prompt) {
                        chatInput.focus();

                        const nativeInputValueSetter =
                            Object.getOwnPropertyDescriptor(
                                window.HTMLTextAreaElement.prototype,
                                "value"
                            )?.set;

                        nativeInputValueSetter?.call(
                            chatInput,
                            currentStep.stepData.prompt
                        );

                        chatInput.dispatchEvent(
                            new Event("input", { bubbles: true })
                        );
                        chatInput.dispatchEvent(
                            new Event("change", { bubbles: true })
                        );
                    }

                    // Move to next step
                    setTimeout(() => {
                        setCurrentGuidedStep(currentGuidedStep + 1);
                        showNextGuidedStep(currentGuidedStep + 1);
                    }, 1000);
                    break;
                }

                case "preview-step": {
                    // User clicked preview, move to next step
                    setTimeout(() => {
                        setCurrentGuidedStep(currentGuidedStep + 1);
                        showNextGuidedStep(currentGuidedStep + 1);
                    }, 500);
                    break;
                }

                case "add-trigger": {
                    // Handle trigger addition
                    setTimeout(() => {
                        setCurrentGuidedStep(currentGuidedStep + 1);
                        showNextGuidedStep(currentGuidedStep + 1);
                    }, 500);
                    break;
                }

                case "generating-code": {
                    // User clicked preview, move to next step
                    setTimeout(() => {
                        setCurrentGuidedStep(currentGuidedStep + 1);
                        showNextGuidedStep(currentGuidedStep + 1);
                    }, 500);
                    break;
                }

                case "send-prompt": {
                    // User clicked preview, move to next step
                    setTimeout(() => {
                        setCurrentGuidedStep(currentGuidedStep + 1);
                        showNextGuidedStep(currentGuidedStep + 1);
                    }, 500);
                    break;
                }

                case "run-code": {
                    // User clicked preview, move to next step
                    setTimeout(() => {
                        setCurrentGuidedStep(currentGuidedStep + 1);
                        showNextGuidedStep(currentGuidedStep + 1);
                    }, 500);
                    break;
                }

                case "info": {
                    // User acknowledged info modal, move to next step
                    setTimeout(() => {
                        setCurrentGuidedStep(currentGuidedStep + 1);
                        showNextGuidedStep(currentGuidedStep + 1);
                    }, 500);
                    break;
                }

                default: {
                    setTimeout(() => {
                        setCurrentGuidedStep(currentGuidedStep + 1);
                        showNextGuidedStep(currentGuidedStep + 1);
                    }, 500);
                    break;
                }
            }
        },
        [
            isGuidedMode,
            waitingForAction,
            guidedSteps,
            currentGuidedStep,
            showNextGuidedStep,
        ]
    );

    // Skip guided mode
    const skipGuidedMode = useCallback(() => {
        setIsGuidedMode(false);
        setIsTooltipVisible(false);
        setCurrentGuidedStep(0);
        setWaitingForAction(null);
        setHasInitializedGuided(false);
        clearTutorialData();
        markTutorialComplete({
            tutorialKey: "welcome",
            completed: true,
        });
    }, [clearTutorialData]);

    // Check for guided mode initialization
    const checkAndInitializeGuidedMode = useCallback(
        (searchParams) => {
            if (automationPlan && !hasInitializedGuided) {
                const isGuided = searchParams.get("guided") === "true";
                const hasPlan = searchParams.get("plan") === "true";

                if (isGuided && hasPlan && automationPlan) {
                    setHasInitializedGuided(true);
                    initializeGuidedMode(automationPlan);
                }
            }
        },
        [automationPlan, hasInitializedGuided, initializeGuidedMode]
    );

    // Initialize guided mode when automation plan is available (only once)
    React.useEffect(() => {
        if (automationPlan && !hasInitializedGuided) {
            const searchParams = new URLSearchParams(window.location.search);
            const isGuided = searchParams.get("guided") === "true";
            const hasPlan = searchParams.get("plan") === "true";

            if (isGuided && hasPlan) {
                setHasInitializedGuided(true);
                initializeGuidedMode(automationPlan);
            }
        }
    }, [automationPlan, hasInitializedGuided, initializeGuidedMode]);

    return {
        selectedAutomation,
        automationPlan,
        isTutorialActive,
        tutorialStep,
        selectAutomation,
        setTutorialPlan,
        completeTutorialWithPlan,
        clearTutorialData,
        updateTutorialStep,
        // Guided automation
        isGuidedMode,
        currentGuidedStep,
        guidedSteps,
        tooltipPosition,
        isTooltipVisible,
        waitingForAction,
        initializeGuidedMode,
        showNextGuidedStep,
        handleGuidedAction,
        completeGuidedMode,
        skipGuidedMode,
        checkAndInitializeGuidedMode,
        // Completion modal
        showCompletionModal,
        handleMeetAutomationGuide,
        handleContinueToWebTour,
        closeCompletionModal,
    };
}

export function ProvideTutorial({ children }) {
    const tutorial = useProvideTutorial();
    const { t } = useTranslation();

    return (
        <tutorialContext.Provider value={tutorial}>
            {/* Guided Automation Tooltip */}
            <GuidedTooltip
                step={tutorial.guidedSteps[tutorial.currentGuidedStep]}
                position={tutorial.tooltipPosition}
                onNext={() =>
                    tutorial.handleGuidedAction(
                        tutorial.guidedSteps[tutorial.currentGuidedStep]?.action
                    )
                }
                onSkip={tutorial.skipGuidedMode}
                isVisible={tutorial.isTooltipVisible}
            />

            {/* Tutorial Completion Modal */}
            <Dialog
                open={tutorial.showCompletionModal}
                onOpenChange={tutorial.closeCompletionModal}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader className="text-center">
                        <div className="flex justify-center mb-4">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-10 h-10 text-green-600" />
                            </div>
                        </div>
                        <DialogTitle className="text-2xl font-bold text-center">
                            {t("tutorialContext.completionModal.title")}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="text-center space-y-6">
                        <p className="text-lg text-gray-600">
                            {t("tutorialContext.completionModal.message")}
                        </p>

                        {/* Progress bar placeholder */}
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-gray-400 h-2 rounded-full w-full"></div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                            <Button
                                variant="outline"
                                onClick={tutorial.handleMeetAutomationGuide}
                                className="flex-1"
                            >
                                {t("tutorialContext.completionModal.meetGuide")}
                            </Button>
                            <Button
                                onClick={tutorial.closeCompletionModal}
                                className="flex-1"
                            >
                                {t("tutorialContext.completionModal.done")}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {children}
        </tutorialContext.Provider>
    );
}

export function useTutorial() {
    return useContext(tutorialContext);
}
